import { useMemo, useState, useEffect } from 'react';
import PlaylistCard from '../Playlists/PlaylistCard';

// Petit hook de debounce local
function useDebouncedValue(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function PlaylistSearch({
  playlistsData,
  loading,
  searchProps,
  setSearchProps,
  onSoftDelete,
  onRestore,
}) {
  const { search, category, provider, sortBy } = searchProps;

  // 👇 Saisie locale, indépendante de l’état “commité”
  const [localSearch, setLocalSearch] = useState(search || '');
  useEffect(() => setLocalSearch(search || ''), [search]);

  const debouncedLocal = useDebouncedValue(localSearch, 1000);
  const MIN_CHARS = 2;

  // 👇 Commit auto seulement si 0 ou ≥ MIN_CHARS
  useEffect(() => {
    const canQuery =
      (debouncedLocal?.length || 0) === 0 ||
      (debouncedLocal?.length || 0) >= MIN_CHARS;

    if (!canQuery) return;
    if (debouncedLocal === search) return;

    setSearchProps(sp => ({ ...sp, search: debouncedLocal }));
  }, [debouncedLocal, search, setSearchProps]);

  const allCategories = useMemo(() => {
    const cats = {};
    (playlistsData || []).forEach(pl =>
      Object.values(pl.activityCategories || {}).forEach(cat => {
        if (cat && cat.label) cats[cat.label] = cat.label;
      })
    );
    return Object.keys(cats);
  }, [playlistsData]);

  const allProviders = useMemo(() => {
    const prov = {};
    (playlistsData || []).forEach(pl =>
      Object.values(pl.activityCategories || {}).forEach(cat => {
        Object.values(cat.bySmtpProvider || {}).forEach(smtp => {
          if (smtp && smtp.name && smtp.name !== 'Non défini')
            prov[smtp.name] = smtp.name;
        });
      })
    );
    return Object.keys(prov);
  }, [playlistsData]);

  return (
    <div>
      <div
        className="flex flex-wrap gap-4 mt-0 mb-4 items-end"
        style={{
          background: 'white',
          padding: '50px',
          border: '#c1c1c154 1px solid',
          borderRadius: '0 10px',
          marginTop: '-35px',
        }}
      >
        <div className="flex-1 min-w-[200px]">
          <input
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
            placeholder="Recherche playlist ou broadcast actif..."
            className="border rounded p-2 w-full"
            disabled={loading}
          />
          {/* <div className="text-xs text-slate-500 mt-1">
            Tapez <strong>au moins {MIN_CHARS}</strong> caractères (ou vide)
            pour lancer la recherche.
          </div> */}
        </div>

        <div>
          <select
            value={category}
            onChange={e =>
              setSearchProps(sp => ({ ...sp, category: e.target.value }))
            }
            className="border rounded p-2 min-w-[120px]"
            disabled={loading}
          >
            <option value="">Toutes catégories</option>
            {allCategories.map(label => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <select
            value={provider}
            onChange={e =>
              setSearchProps(sp => ({ ...sp, provider: e.target.value }))
            }
            className="border rounded p-2 min-w-[120px]"
            disabled={loading}
          >
            <option value="">Tous providers</option>
            {allProviders.map(p => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div>
          <select
            value={sortBy}
            onChange={e =>
              setSearchProps(sp => ({ ...sp, sortBy: e.target.value }))
            }
            className="border rounded p-2 min-w-[220px]"
            disabled={loading}
          >
            <option value="active">Nb broadcasts actifs</option>
            <option value="upcoming">Démarrent bientôt</option>
            <option value="name">Nom de playlist (A-Z)</option>
          </select>
        </div>
      </div>

      {!loading && (!playlistsData || playlistsData.length === 0) && (
        <div className="text-gray-500 italic text-center mt-12">
          Aucune playlist ne correspond à vos critères.
        </div>
      )}

      {!loading &&
        (playlistsData || []).map(pl => (
          <PlaylistCard
            key={pl.id}
            name={pl.name}
            playlistId={pl.id}
            activityCategories={pl.activityCategories}
            upcomingCategories={pl.upcomingCategories}
            inactiveCategories={pl.inactiveCategories}
            global={pl.global}
            deletedAt={pl.deletedAt}
            onSoftDelete={() => onSoftDelete?.(pl.id)}
            onRestore={() => onRestore?.(pl.id)}
          />
        ))}
    </div>
  );
}
